import {
  disableFormSubmit,
  enableFormSubmit,
} from './utils/form-validation-utils';
import { getParameterByName } from './utils/url-utils';
import {
  retreiveFromSessionStorage,
  saveToSessionStorage,
} from './utils/browser-utils';

export default class GrecaptchaApi {
  constructor() {
    const { grecaptcha } = window;
    this.grecaptcha = grecaptcha;
    this.apiScript = document.getElementById('grecaptcha-api');
    this.assessmentUrl = '/GetRecaptchaAssessmentScore';
    this.siteKeysUrl = '/InitializeRecaptcha';
    this.actionLogsUrl = '/GetRecaptchaActionLogs';
    this.challengeKey = '';
    this.challengeUrl = '';
    this.scoreKey = '';
    this.scoreThreshold = this.getScoreThreshold();
    this.currentScore = -1;
    this.addOnClickActionLogs();
    this.initializeRecaptchaForms();
  }

  initializeRecaptchaForms() {
    const that = this;
    // If page has form(s) containing a recaptcha challenge,
    // disable the submit buttons until the getScore() callback has fired
    const recaptchaElements = document.querySelectorAll('[data-challenge-id]');
    [...recaptchaElements].forEach((element, index) => {
      // Prepare recaptcha targets
      if (!element.id) {
        element.id = `grecaptcha-container-${index}`;
      }
      const form = element.closest('form');
      if (form === null || form === undefined) return;
      const recaptchaChallengeIpt = form.querySelector('#RecaptchaChallenge');
      recaptchaChallengeIpt.classList.add('recaptcha-challenge-input');
      // If there is more than one challenge on a page, we *should* to update the id
      // of the challenge input to be unique
      if (recaptchaElements.length > 1) {
        // Update id on label
        const label = form.querySelector('[for="RecaptchaChallenge"]');
        label.setAttribute('for', `RecaptchaChallenge${index}`);
        // Update id on input
        recaptchaChallengeIpt.id = `RecaptchaChallenge${index}`;
      }
      // Standard (non-AJAX) posts that carry a RecaptchaScoreToken field (e.g. the login page
      // form) must send a fresh score token so the server can re-assess the action; wire that up.
      that.attachScoreTokenSubmit(form);
      disableFormSubmit(form);
    });
    // Get score and determine whether or not to show recaptcha challenge
    this.getScore({
      action: 'page_load',
      callback: () => {
        // Reenable the submit button and initialize the challenge, if required, on those forms
        [...recaptchaElements].forEach((element) => {
          const form = element.closest('form');
          if (form === null || form === undefined) return;
          // Get challenge size from form data attribute, if exists
          const { recaptchaChallengeSize } = element.dataset;
          const challengeSize = recaptchaChallengeSize;
          enableFormSubmit(form);
          that.enableFormRecaptcha({ challengeSize, form });
        });
      },
    });
  }

  // Mints a fresh reCAPTCHA Enterprise score token for the given action. Ensures the site keys are
  // loaded first (cached in sessionStorage by getKeys), then resolves with the token string.
  mintScoreToken(action) {
    return this.getKeys().then((keys) => {
      this.scoreKey = keys.scoreKey;
      return this.getToken(action, this.scoreKey);
    });
  }

  // For a standard (non-AJAX) form post that includes a RecaptchaScoreToken field, intercept the
  // submit to mint a fresh score token for the form's action (data-recaptcha-action, default
  // "login") and write it into that field before submitting. If an interactive challenge is
  // displayed but not yet solved, block the submit and surface the challenge error instead --
  // the server also enforces this, but blocking here gives immediate feedback. Fails closed: if a
  // score token cannot be obtained the field is left empty and the server rejects the login.
  attachScoreTokenSubmit(form) {
    if (form === null || form === undefined) return;
    // When the server does not enforce login reCAPTCHA (e.g. Dev), don't intercept the submit at
    // all so a low-score challenge cannot block local login/testing.
    if (window.loginRecaptchaEnabled === false) return;
    const scoreInput = form.querySelector('input[name="Input.RecaptchaScoreToken"]');
    if (!scoreInput) return;
    const action = form.dataset.recaptchaAction || 'login';
    let tokenReady = false;
    form.addEventListener('submit', (event) => {
      // Second pass (after the token has been minted): allow the native submit through.
      if (tokenReady) {
        return;
      }
      event.preventDefault();
      // If a challenge is on screen and not completed, do not submit.
      const challengeWrapper = form.querySelector('.grecaptcha-challenge-wrapper');
      const challengeInput = form.querySelector('.recaptcha-challenge-input');
      const challengeVisible = challengeWrapper && !challengeWrapper.classList.contains('hidden');
      if (challengeVisible && (!challengeInput || challengeInput.value === '')) {
        const errorEl = challengeWrapper.querySelector('[id^="recaptchaError"]');
        if (errorEl) {
          errorEl.classList.remove('field-validation-valid');
          errorEl.classList.add('field-validation-error');
        }
        return;
      }
      const proceed = (token) => {
        scoreInput.value = token || '';
        tokenReady = true;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      };
      this.mintScoreToken(action)
        .then(proceed)
        .catch(() => proceed(''));
    });
  }

  displayChallenge(score) {
    const { scoreThreshold } = this;
    return score < scoreThreshold;
  }

  enableFormRecaptcha(options) {
    const {
      challengeSize,
      form,
    } = options;
    if (form === null) {
      return;
    }
    const challengeIdContainer = form.querySelector('[data-challenge-id]');
    if (challengeIdContainer === null) {
      return;
    }
    const challengeId = challengeIdContainer.id;
    const recaptchaChallengeIpt = form.querySelector('.recaptcha-challenge-input');
    if (recaptchaChallengeIpt === null) {
      return;
    }
    // When the server doesn't enforce login reCAPTCHA (e.g. Dev), don't render a challenge on the
    // login form -- otherwise a low Dev score shows a widget that isn't required, which is confusing.
    // Mark the field with the sentinel so any client-side "required" validation still passes.
    if (form.dataset && form.dataset.recaptchaAction === 'login' && window.loginRecaptchaEnabled === false) {
      recaptchaChallengeIpt.value = 'no-challenge-needed';
      return;
    }
    if (recaptchaChallengeIpt.value !== '') {
      return;
    }
    if (this.displayChallenge(this.currentScore)) {
      this.getChallenge({
        callback: (widgetId) => {
          const wrapper = form.querySelector('.grecaptcha-challenge-wrapper');
          wrapper.classList.remove('hidden');
          // Remember the rendered widget so a failed AJAX login (modal) can reset it for a retry.
          form.recaptchaWidgetId = widgetId;
        },
        challengeId,
        challengeSize,
        verifyCallback: (response) => {
          recaptchaChallengeIpt.value = response;
        },
      });
    } else {
      recaptchaChallengeIpt.value = 'no-challenge-needed';
    }
  }

  // Resets a form's interactive challenge (reCAPTCHA tokens are single-use, so a failed AJAX login
  // must re-solve). Clears the stored token and unchecks the widget so verifyCallback fires afresh.
  resetChallenge(form) {
    if (!form) return;
    const input = form.querySelector('.recaptcha-challenge-input');
    if (input) {
      input.value = '';
    }
    const { grecaptcha } = this;
    const widgetId = form.recaptchaWidgetId;
    if (grecaptcha && grecaptcha.enterprise && widgetId !== undefined && widgetId !== null) {
      try {
        grecaptcha.enterprise.reset(widgetId);
      } catch (e) {
        // reCAPTCHA not ready / invalid widget id -- nothing to reset.
      }
    }
  }

  getKeys() {
    // Check sessionStorage for site keys
    // If they exist, pass along
    const keys = retreiveFromSessionStorage('__grecaptchakeys');
    if (keys) {
      const {
        challengeKey,
        challengeUrl,
        scoreKey,
      } = keys;
      if (challengeKey && challengeUrl && scoreKey) {
        return new Promise((resolve) => {
          // console.log('grecaptcha ----> get keys from sessionStorage');
          resolve(keys);
        });
      }
    }
    // console.log('grecaptcha ----> only execute if no keys found in sessionStorage');
    const url = this.siteKeysUrl;
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'POST',
      })
        .then((response) => {
          if (response.ok) {
            response.json().then((data) => {
              saveToSessionStorage('__grecaptchakeys', data);
              resolve(data);
            });
          } else {
            response.json().then(reject);
          }
        })
        .catch(console.error);
    });
  }

  getToken(action, sitekey) {
    const { grecaptcha } = this;
    return new Promise((resolve, reject) => {
      grecaptcha.enterprise.ready(() => {
        grecaptcha.enterprise.execute(sitekey, { action })
          .then((data) => {
            if (data) {
              resolve(data);
            } else {
              reject();
            }
          });
      });
    });
  }

  getAssessmentScore(action, token) {
    const url = this.assessmentUrl;
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expectedAction: action,
          token,
        }),
      })
        .then((response) => {
          if (response.ok) {
            response.json().then(resolve);
          } else {
            response.text().then(reject);
          }
        })
        .catch(console.error);
    });
  }

  getChallenge(options) {
    const {
      challengeKey,
      challengeUrl,
      grecaptcha,
    } = this;
    const {
      callback,
      challengeId,
      challengeSize,
      verifyCallback,
    } = options;
    if (!grecaptcha) {
      return -1;
    }
    // Add script
    const grecaptchaChallengeScript = document.createElement('script');
    grecaptchaChallengeScript.src = challengeUrl;
    document.body.appendChild(grecaptchaChallengeScript);
    grecaptchaChallengeScript.addEventListener('load', () => {
      const recaptchaChallenge = grecaptcha.enterprise.render(challengeId, {
        callback: verifyCallback,
        sitekey: challengeKey,
        size: challengeSize || 'normal',
      });
      if (callback) {
        callback(recaptchaChallenge);
      }
    });
    return false;
  }

  logAction(options) {
    const { grecaptcha } = this;
    const {
      action,
      callback,
    } = options;
    if (!grecaptcha) {
      if (callback) {
        callback('');
      }
      return;
    }
    // Get keys
    this.getKeys()
      .then((keys) => {
        const {
          challengeKey,
          challengeUrl,
          scoreKey,
        } = keys;
        this.challengeKey = challengeKey;
        this.challengeUrl = challengeUrl;
        this.scoreKey = scoreKey;
        this.getToken(action, this.scoreKey)
          .then((token) => {
            if (callback) {
              callback(token);
            }
          })
          .catch(() => {
            if (callback) {
              callback('');
            }
          });
      });
  }

  getScoreThreshold() {
    const { apiScript } = this;
    let scoreThreshold = '';
    if (apiScript) {
      scoreThreshold = getParameterByName('scoreThreshold', apiScript.src);
    }
    return scoreThreshold !== '' ? scoreThreshold : '.5';
  }

  /*
    Returns an assessment score for the likelihood user is a human
    Error Codes:
    0: Error retrieving token, show highest form of RECAPTCHA
    -1: Error loading RECAPTCHA
    -2: Asssessment service not available
    -3: No score available
    -4: expectedAction returned from the RECAPTCHA endpoint does not match submitted action
  */
  getScore(options) {
    const { grecaptcha } = this;
    const {
      action,
      callback,
    } = options;
    if (!grecaptcha || !callback) {
      this.currentScore = -1;
      return this.currentScore;
    }
    // Get keys
    this.getKeys()
      .then((keys) => {
        const {
          challengeKey,
          challengeUrl,
          scoreKey,
        } = keys;
        this.challengeKey = challengeKey;
        this.challengeUrl = challengeUrl;
        this.scoreKey = scoreKey;
        this.getToken(action, this.scoreKey)
          .then((token) => {
            this.getAssessmentScore(action, token)
              .then((data) => {
                try {
                  const { event, riskAnalysis } = data;
                  const { score } = riskAnalysis;
                  const { expectedAction } = event;
                  if (expectedAction !== action) {
                    this.currentScore = -4;
                    callback(this.currentScore);
                  }
                  this.currentScore = score;
                  callback(score);
                } catch (error) {
                  this.currentScore = -3;
                  callback(this.currentScore);
                }
              })
              .catch(() => {
                this.currentScore = -2;
                callback(this.currentScore);
              });
          })
          .catch((error) => {
            console.log(error);
            this.currentScore = 0;
            callback(this.currentScore);
          });
      });
    this.currentScore = -1;
    return this.currentScore;
  }

  getRecaptchaActionLogs() {
    const url = this.actionLogsUrl;
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'POST',
      })
        .then((response) => {
          if (response.ok) {
            response.json().then(resolve);
          } else {
            response.json().then(reject);
          }
        })
        .catch(console.error);
    });
  }

  addOnClickActionLogs() {
    const that = this;
    this.getRecaptchaActionLogs()
      .then((actions) => {
        actions.forEach((item) => {
          const {
            action,
            url,
          } = item;
          document.addEventListener('click', (event) => {
            const { target } = event;
            const { href } = target;
            if (href && href === url) {
              that.logAction({ action });
            }
          });
        });
      });
  }
}

window.grecaptchaApi = new GrecaptchaApi();
// Can probably delete this callback. Unsure if it's being referenced by the endpoint
window.onRecaptchaChallengeLoad = () => {
  console.log('window ---> onRecaptchaChallengeLoad');
};
